<main id="main-content" class="h-screen flex flex-col pl-10 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    {{ pageTitle }}
                </h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip" class="absolute z-40 invisible inline-block w-fit max-w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">

                    <div 
                        *ngIf="currentPageRoute == 'statement-of-commitment'"
                        class="px-3 py-2"
                    >
                        <p>
                            Provide an introductory statement that includes philosophy, expectations, and directives to school staff, students, and parents. Click 'Confirm' to confirm the statement if no changes are needed.
                        </p>
                    </div>

                    <div 
                        *ngIf="currentPageRoute == 'purpose-situation-overview'"
                        class="px-3 py-2"
                    >
                        <p>
                            In the Document section, click on each section to highlight and begin filling out the information. For each section, simply provide the information required in the textbox. When complete, click 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>. Each of these documents also requires approval. After completion, an authorized administrator should approve the document.
                        </p>
                    </div>

                    <div 
                        *ngIf="currentPageRoute == 'concept-of-operations' || 
                            currentPageRoute == 'organization-assignment-of-responsibilities' ||
                            currentPageRoute == 'direction-control-coordination' ||
                            currentPageRoute == 'information-collection-analysis-dissemination' ||
                            currentPageRoute == 'administration-finance-logistics' ||
                            currentPageRoute == 'plan-development-maintenance' ||
                            currentPageRoute == 'authorities-references'"
                        class="px-3 py-2"
                    >
                        <p>
                            Complete the textbox. When completed, click on 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>. This document also requires approval. After completion, an authorized administrator should approve the document.
                        </p>
                    </div>

                    <div
                        *ngIf="currentPageRoute == 'training-exercises'"
                        class="px-3 py-2"
                    >
                        <p>
                            View the logs below that will be included in your institution's <strong>Emergency Operations Plan</strong>. To make changes to this log, please visit the <strong>Calendar</strong>.
                        </p>
                    </div>

                    <!-- GA EOP general document pages (no approval step) -->
                    <div
                        *ngIf="currentPageRoute == 'purpose' ||
                            currentPageRoute == 'security-issues-in-school-safety-zone' ||
                            currentPageRoute == 'school-visitors' ||
                            currentPageRoute == 'student-sign-in-and-out-procedure' ||
                            currentPageRoute == 'medications' ||
                            currentPageRoute == 'emergency-status-cards'"
                        class="px-3 py-2"
                    >
                        <p>
                            Complete the textbox. When completed, click on
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>.
                        </p>
                    </div>

                    <!-- MS EOP textbox pages -->
                    <div
                        *ngIf="currentPageRoute == 'mission-statement' ||
                            currentPageRoute == 'reunification-plan' ||
                            currentPageRoute == 'ada-special-needs-considerations-plan' ||
                            currentPageRoute == 'general-response-duties' ||
                            currentPageRoute == 'fire-explosion' ||
                            currentPageRoute == 'fire-explosion-near-the-school' ||
                            currentPageRoute == 'severe-weather' ||
                            currentPageRoute == 'intruder-lockdown-controlled-movement' ||
                            currentPageRoute == 'earthquake' ||
                            currentPageRoute == 'bomb-threat' ||
                            currentPageRoute == 'gas-leak-gas-odor' ||
                            currentPageRoute == 'hazardous-materials' ||
                            currentPageRoute == 'after-school-care'"
                        class="px-3 py-2"
                    >
                        <p>
                            Complete the textbox. When completed, click on
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>. This document also requires approval. After completion, an authorized administrator should approve the document.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                *ngIf="selectedDoc !== 'Record of Distribution' && currentPageRoute !== 'training-exercises'"
                [sectionId]="eopSectionTypeId"
                [subSectionId]="eopSubSectionTypeId"
            ></eop-assistant>
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-col flex-grow items-start overflow-y-auto">
        <div class="flex items-start justify-start gap-1">
            <button 
                *ngFor="let doc of documents"
                class="min-w-32 px-3 py-2 rounded-t-lg oswald-font shadow-custom-2 text-lg text-center cursor-pointer"
                [ngClass]="{
                    'bg-white': selectedDoc === doc,
                    'bg-gradient-to-b from-nside-gray-100 to-nside-gray-200 text-white': selectedDoc !== doc
                    }"
                (click)="$event.preventDefault(); selectDoc(doc);"
            >
                {{ doc.includes('-') ? doc.split('-')[1].trim() : doc }}
            </button>
        </div>
        <div 
            class="w-full h-full flex flex-col items-center justify-start gap-4"
        >
            <div class="w-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col flex-grow gap-4 overflow-y-auto">

                <!-- TEXTBOX SECTION -->
                <div 
                    class="w-full h-full flex flex-col items-center gap-4 overflow-y-auto"
                >
                    <form
                        class="w-full h-full flex flex-col gap-2"
                        (ngSubmit)="onSubmit()"
                        #promulgationForm="ngForm"
                        >
                        <div class="p-1 w-full flex-grow flex flex-col gap-2">
                            <textarea
                                *ngIf="canSave()"
                                name="description"
                                [(ngModel)]="description"
                                rows="10"
                                class="w-full h-full flex-grow bg-gray-50 p-2 text-sm text-gray-900 rounded-md border border-gray-300 resize-none ring-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Type here..."
                                required
                            ></textarea>
                            <p
                                *ngIf="!canSave()"
                                class="w-full h-full flex-grow p-2 text-sm whitespace-pre-line break-words overflow-y-auto"
                                [ngClass]="description ? 'text-gray-900' : 'text-gray-400 italic'"
                            >{{ description || 'Empty' }}</p>
                        </div>

                        <div class="w-full flex items-center justify-end">
                            <div *ngIf="eopDoc?.last_updated_at" class="w-full flex gap-1 items-start justify-start">
                                <p class="oswald-font font-normal">
                                    Last Updated At: <span class="raleway-font">{{ eopDoc?.last_updated_at | date: 'MM/dd/yyyy - HH:mm' }}</span>
                                </p>
                                <p class="oswald-font font-normal">
                                    By: <span class="raleway-font">{{ eopDoc?.last_updated_user_name || 'None' }}</span>
                                </p>
                            </div>
                            <button
                                *ngIf="canSave()"
                                type="submit"
                                class="w-20 flex items-center justify-center text-white oswald-font px-4 py-1.5 bg-gradient-to-b rounded-md"
                                [ngClass]="{
                                    'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !description.trim() || isContentUnchanged,
                                    'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': description.trim() && !isContentUnchanged
                                }"
                                [disabled]="!description.trim() || isContentUnchanged"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- APPROVE DOCUMENT BUTTON -->
            <div
                *ngIf="!isGaPlan"
                class="w-full bg-white rounded-md shadow-custom-2 p-6 flex gap-4 overflow-y-auto"
            >
                <div
                    class="w-full p-2 border-l-4 bg-gray-50 rounded-r-md flex items-end gap-2"
                    [ngClass]="{
                        'border-green-600': eopDoc?.user_name_approved,
                        'border-nside-gray-300': !eopDoc?.user_name_approved
                    }"
                >
                    <div class="w-full flex flex-col items-start justify-between">
                        <p class="oswald-font font-normal">
                            Confirmed By: <span class="raleway-font">{{ eopDoc?.user_name_approved || 'Not Confirmed Yet' }}</span>
                        </p>
                        <p class="oswald-font font-normal">
                            Date Confirm: <span class="raleway-font">{{ eopDoc?.date_approved | date: 'MM/dd/yyyy - HH:mm' }}</span>
                        </p>
                        <p *ngIf="canSave()" class="w-full text-end">
                            By clicking this button, I certify that this information is correct and does not need to be updated.
                        </p>
                    </div>
                    <button
                        *ngIf="canSave()"
                        class="w-20 flex items-center justify-center text-white oswald-font px-4 py-1.5 bg-gradient-to-b rounded-md"
                        [ngClass]="{
                            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !eopDoc?.description,
                            'border-green-600 from-green-500 to-green-600 hover:from-green-500 hover:to-green-700': eopDoc?.description
                        }"
                        [disabled]="!eopDoc?.description"
                        (click)="$event.preventDefault(); handleApproval();"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    </div>

    <prv-next-buttons></prv-next-buttons>
</main>

<introduction></introduction>

<popup-message 
    [type]="messageModal"
    [message]="popupMessage"
    (close)="resetMessageModal()"
></popup-message>

<div *ngIf="loading">
    <spinner></spinner>
</div>
